import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  PublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalConfig, loginRequest, graphConfig, dataverseScopes } from '../services/authConfig';
import { setAccessToken } from '../services/api';

// Check if auth is enabled via environment variable
const isAuthEnabled = import.meta.env.VITE_ENABLE_AUTH === 'true';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL - required for v3+
const msalInitPromise = isAuthEnabled ? msalInstance.initialize() : Promise.resolve();

// User type
export interface User {
  id: string;
  displayName: string;
  email: string;
  photoUrl?: string;
}

// Auth context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: (scopes: string[]) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user for development
const mockUser: User = {
  id: 'mock-user-1',
  displayName: 'Dev User',
  email: 'dev@localhost',
  photoUrl: undefined,
};

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Inner component that uses MSAL hooks (only when auth is enabled)
function MsalAuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Acquire Dataverse access token and set it for the API
  const acquireDataverseToken = async (account: AccountInfo) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...dataverseScopes,
        account,
      });
      setAccessToken(tokenResponse.accessToken);
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(dataverseScopes);
      }
      console.error('Error acquiring Dataverse token:', error);
      return null;
    }
  };

  // Fetch user profile from Graph API
  const fetchUserProfile = async (account: AccountInfo) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const response = await fetch(graphConfig.graphMeEndpoint, {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      });

      if (response.ok) {
        const profile = await response.json();

        // Try to fetch photo
        let photoUrl: string | undefined;
        try {
          const photoResponse = await fetch(graphConfig.graphPhotoEndpoint, {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
          });
          if (photoResponse.ok) {
            const blob = await photoResponse.blob();
            photoUrl = URL.createObjectURL(blob);
          }
        } catch {
          // Photo not available
        }

        setUser({
          id: profile.id,
          displayName: profile.displayName,
          email: profile.mail || profile.userPrincipalName,
          photoUrl,
        });

        // Acquire Dataverse token after user profile is set
        await acquireDataverseToken(account);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Wait for MSAL to initialize first
        await msalInitPromise;
        console.log('MSAL initialized');

        // Handle redirect response
        const response = await instance.handleRedirectPromise();
        console.log('Redirect response:', response);

        if (response) {
          // We got a response from redirect - use that account
          console.log('Setting account from redirect response');
          instance.setActiveAccount(response.account);
          await fetchUserProfile(response.account);
        } else {
          // Check for existing accounts
          const currentAccounts = instance.getAllAccounts();
          console.log('Current accounts:', currentAccounts.length);
          if (currentAccounts.length > 0) {
            instance.setActiveAccount(currentAccounts[0]);
            await fetchUserProfile(currentAccounts[0]);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [instance]);

  useEffect(() => {
    if (accounts.length > 0 && !user) {
      fetchUserProfile(accounts[0]);
    }
  }, [accounts]);

  const login = async () => {
    try {
      // Ensure MSAL is initialized before login
      await msalInitPromise;
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const logout = async () => {
    try {
      // Clear the Dataverse access token
      setAccessToken(null);
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getAccessToken = async (scopes: string[]): Promise<string | null> => {
    const account = instance.getActiveAccount();
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        scopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ scopes });
      }
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Mock auth provider for development
function MockAuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => setIsLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  const mockAuthContext: AuthContextType = {
    user: mockUser,
    isAuthenticated: true,
    isLoading,
    login: async () => {
      console.log('Mock login - auth disabled');
    },
    logout: async () => {
      console.log('Mock logout - auth disabled');
    },
    getAccessToken: async () => {
      console.log('Mock getAccessToken - auth disabled');
      return null;
    },
  };

  return (
    <AuthContext.Provider value={mockAuthContext}>
      {children}
    </AuthContext.Provider>
  );
}

// Main AuthProvider that switches between real and mock auth
export function AuthProvider({ children }: { children: ReactNode }) {
  if (!isAuthEnabled) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthProvider>{children}</MsalAuthProvider>
    </MsalProvider>
  );
}
